import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';

import type { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition';
import type { PublicLinkPageStatus } from '@confluence/public-links';

// Generated from: https://hello.atlassian.net/wiki/spaces/PGT/pages/403508651/Anon+-+Analytics

type EventType = 'sendUIEvent' | 'sendTrackEvent';

type EventScreen = 'shareButtonDialog' | 'shareModal' | 'viewPageScreen' | 'editPageScreen';

type EventAction = 'viewed' | 'clicked' | 'updated' | 'shown';

type EventSubject = 'button' | 'toggle' | 'pagePublicLinksSettings' | 'editPageScreen' | 'page';

type EventSubjectId =
	| 'pagePublicLinksToggle'
	| 'confirmDisablePagePublicLinksButton'
	| 'cancelTogglePagePublicLinksButton'
	| 'getAccessToViewMacroButton'
	| string;

interface AnalyticsDataPayload {
	source?: EventScreen;
	action: EventAction;
	actionSubject: EventSubject;
	actionSubjectId?: EventSubjectId;
}

export type AnalyticsEventEmitter<T> = T extends {}
	? (createAnalyticsEvent: CreateUIAnalyticsEvent, attributes: T) => void
	: (createAnalyticsEvent: CreateUIAnalyticsEvent) => void;

const eventFactory = <T = unknown>(
	type: EventType,
	data: AnalyticsDataPayload,
	constantAttributes?: Record<string, unknown>,
): AnalyticsEventEmitter<T> =>
	((createAnalyticsEvent: CreateUIAnalyticsEvent, attributes?: T) =>
		createAnalyticsEvent({
			type,
			data: {
				...data,
				attributes: {
					...constantAttributes,
					...attributes,
				},
			},
		}).fire()) as AnalyticsEventEmitter<T>;

type AnalyticsEventEmitterPageSubjectId<T> = T extends {}
	? (
			createAnalyticsEvent: CreateUIAnalyticsEvent,
			spaceId: string | undefined | null,
			contentId: string | undefined | null,
			attributes: T,
		) => void
	: (
			createAnalyticsEvent: CreateUIAnalyticsEvent,
			spaceId: string | undefined | null,
			contentId: string | undefined | null,
		) => void;

const eventFactoryPageSubjectId = <T = unknown>(
	type: EventType,
	data: AnalyticsDataPayload,
): AnalyticsEventEmitterPageSubjectId<T> =>
	((
		createAnalyticsEvent: CreateUIAnalyticsEvent,
		spaceId: string | undefined | null,
		contentId: string | undefined | null,
		attributes?: T,
	) =>
		createAnalyticsEvent({
			type,
			data: {
				containerType: 'space',
				containerId: spaceId,
				objectType: 'page',
				objectId: contentId,
				...data,
				actionSubjectId: contentId,
				attributes,
			},
		}).fire()) as AnalyticsEventEmitterPageSubjectId<T>;

export const analyticsShareModalPageToggle = eventFactory<{
	pageId: string;
	spaceId: string;
	previousState: 'enabled' | 'disabled';
	newState: 'enabled' | 'disabled';
	parentRestricted: boolean;
	localRestricted: boolean;
	edition: ConfluenceEdition | null;
	contentType?: string | null;
	isLivePage?: boolean;
	subType?: string | null;
}>('sendUIEvent', {
	source: 'shareButtonDialog',
	action: 'clicked',
	actionSubject: 'toggle',
	actionSubjectId: 'pagePublicLinksToggle',
});

export const analyticsShareModalConfirmDisablePageButton = eventFactory<{
	pageId: string;
}>('sendUIEvent', {
	source: 'shareButtonDialog',
	action: 'clicked',
	actionSubject: 'button',
	actionSubjectId: 'confirmDisablePagePublicLinksButton',
});

export const analyticsShareModalCancelDisablePageButton = eventFactory<{
	pageId: string;
}>('sendUIEvent', {
	source: 'shareButtonDialog',
	action: 'clicked',
	actionSubject: 'button',
	actionSubjectId: 'cancelTogglePagePublicLinksButton',
});

export const analyticsShareModalPageSettings = eventFactoryPageSubjectId<{
	previousState: PublicLinkPageStatus;
	newState: PublicLinkPageStatus;
	parentRestricted: boolean;
	localRestricted: boolean;
	edition: ConfluenceEdition | null;
}>('sendTrackEvent', {
	// DO NOT CHANGE THIS ANALYTICS EVENT.
	// This shape is necessary to populate the audience for PL changeboarding.
	source: 'shareModal',
	action: 'updated',
	actionSubject: 'pagePublicLinksSettings',
});

export const analyticsEditPageScreenShown = eventFactoryPageSubjectId<{
	isPublicLinkEnabled: boolean;
}>('sendTrackEvent', {
	source: 'editPageScreen',
	action: 'shown',
	actionSubject: 'page',
});

export const analyticsViewPageScreenShown = eventFactoryPageSubjectId<{
	isPublicLinkEnabled: boolean;
}>('sendTrackEvent', {
	source: 'viewPageScreen',
	action: 'shown',
	actionSubject: 'page',
});

export const analyticsViewPageScreen = eventFactoryPageSubjectId<{
	pageType: string | null | undefined;
	daysSinceCreated: number;
	daysSinceModified: number;
	isPublicView: boolean;
	canEdit?: boolean;
	xid: any[];
}>('sendTrackEvent', {
	source: 'viewPageScreen',
	action: 'viewed',
	actionSubject: 'page',
});

export const analyticsViewPageScreenGetAccessToViewMacroButton = eventFactory<{
	macroKey: string;
	extensionKey?: string | number | boolean;
}>(
	'sendUIEvent',
	{
		source: 'viewPageScreen',
		action: 'clicked',
		actionSubject: 'button',
		actionSubjectId: 'getAccessToViewMacroButton',
	},
	{ isPublicView: true },
);
